/* Copyright (c) 2020-present CloudIO Inc */

import type { ItemID } from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { DataRecord, Filters, Row } from '@cloudio-saas/datasource-types';
import type { VariableProvider } from '@cloudio-saas/quickx';
import { atom, StateXGetter } from '@cloudio-saas/statex';
import {
  rootPageAtom,
  rootPageWidthAtom,
  windowSizeAtom,
} from 'hooks/useWindowSize';
import { getItemProp } from 'platform/GetProp';
import { getSessionProperty } from 'root/useUser';
import { EMPTY_ARRAY, NULL_SYMBOL } from 'store/Store';
import { isFromDB, Records } from 'store/storeTypes';
import { getStore } from 'store/useDataStore';
import { NOT_DEFINED } from 'utils/isEmpty';
import { getIn } from '@cloudio-saas/mutx';
import { getURLParam } from 'utils/url';
import Logger from 'utils/Logger';
import { themeAtom } from 'platform/PlatformProvider';
import { localStorageSelector } from './LocalStorage';
import { routeAtom, urlParamsAtom } from '../routes/RouteState';

/**
 * @interface Context
 * The context to be used for the expression evaluation
 * @member appUid The application id. This will be available in the props passed to the component.
 * @member pageId - The page id. This will be available in the props passed to the component.
 * @member itemId - The item id. This will be available in the props passed to the component.
 * @member property - The property for which this expression belongs to.
 * @member data The data to be available for the expression to use. Any value passed
 * in data e.g. `data: {myKey: 'myValue'}` can be accessed in expressions as `$._.myKey`
 * @member recordId - If this component is rendered multiple times once per row of the store, then
 * you can pass the `recordId` for the expression to be evaluated only agains
 * the given row. If no value is passed then the row will be selected based
 * on the rowIndex prop
 * @member rowIndex If this component is rendered multiple times once per row of the store, then
 * you can pass the `rowIndex` for the expression to be evaluated only agains
 * the given row. If no value is passed for rowIndex, then it will be derived
 * using `useRowIndex()` hook. If no rowIndex is available then the expression
 * will be evaluated in the context of the current active record of the store.
 * */
export interface Context {
  appUid: string;
  pageId: ItemID;
  itemId: ItemID;
  property: string;
  data: Record<string, unknown>;
  dsAlias: string;
  recordId?: string;
  rowIndex?: number;
}

const EMPTY_OBJECT = {};

const sourceAtom = atom<Record<string, unknown>>({
  path: ['DnD', 'source'],
  defaultValue: {},
});

const targetAtom = atom<Record<string, unknown>>({
  path: ['DnD', 'target'],
  defaultValue: {},
});

export default class PlatformVariableProvider implements VariableProvider {
  platform: Platform;

  context: Context;

  getter: StateXGetter;

  constructor(platform: Platform, context: Context, getter?: StateXGetter) {
    this.platform = platform;
    this.context = context;
    this.getter = getter ?? platform.getGetter();
  }

  // eslint-disable-next-line class-methods-use-this
  isNested(path: string[]): boolean {
    const len = path.length;
    const key = path[0];
    if (len === 1) {
      if (
        [
          '$page',
          '$pageData',
          '$chartTooltipData',
          '$globalData',
          '$localStorage',
          '$param',
          'screen',
          'session',
          'source',
          '_',
          'target',
          'theme',
        ].includes(key)
      ) {
        return true;
      }
      const { appUid, pageId } = this.context;
      if (this.platform.getDataSourceName(appUid, pageId, key)) {
        return true;
      }
    }
    if (len === 2 && (key === '$page' || key === 'theme')) {
      return true;
    }
    if (len === 2 && path[1] === '$orig') {
      return true;
    }
    if (key === '$chartTooltipData') {
      return len === 1;
    }
    let { data } = this.context;
    const p = [...path];

    if (key === 'theme') {
      data = { theme: this.platform.theme };
    }

    if (key === '_') {
      p.shift();
    }
    const val = getIn(data, p, undefined);
    return val && typeof val === 'object' && !Array.isArray(val);
  }

  get(path: string[]): unknown {
    const { appUid, pageId, data, dsAlias, rowIndex, recordId } = this.context;
    let _dsAlias = dsAlias === NOT_DEFINED ? undefined : dsAlias;
    let attr = path[0];
    let attr2: string | undefined = undefined;
    if (path.length > 1) {
      _dsAlias = path[0];
      attr = path[1];
      attr2 = path[2];
      switch (_dsAlias) {
        case '$page': {
          if (path.length !== 3) {
            throw Error(`Invalid path ${path.join('.')}!`);
          }
          const itemId = path[1];
          const prop = path[2];
          // @ts-ignore prop is string
          return getItemProp(this.getter, appUid, pageId, itemId, prop);
        }
        case '$pageData': {
          return this.getter(['$pageData', appUid, pageId, attr], undefined);
        }
        case '$chartTooltipData': {
          const [, ...others] = path;
          return this.getter(
            ['transient', 'chartjs', 'tooltip', ...others],
            undefined,
          );
        }
        case '$globalData': {
          return this.getter(['$globalData', attr], undefined);
        }
        case '$localStorage': {
          return this.getter(localStorageSelector, {
            params: { appUid, key: attr },
          });
        }
        case '$param': {
          // subscribe to the url params atom
          this.getter([...urlParamsAtom.path, attr]);
          // get the latest value from the url
          return getURLParam(attr);
        }
        case 'screen': {
          const res = { ...this.getter(windowSizeAtom) };
          res.width = this.getter(rootPageWidthAtom);
          switch (attr) {
            case 'width':
            case 'height': {
              return res[attr];
            }
            case 'portrait': {
              return res.height > res.width;
            }
            case 'landscape': {
              return res.height < res.width;
            }
            case 'laptop':
            case 'lg': {
              return res.width >= 992 && res.width < 1200;
            }
            case 'md':
            case 'tablet': {
              return res.width >= 768 && res.width < 992;
            }
            case 'mobile':
            case 'sm': {
              return res.width < 768;
            }
            case 'desktop':
            case 'xl': {
              return res.width >= 1200;
            }
            default:
              throw Error(`Invalid screen property ${attr}!`);
          }
        }
        case 'session': {
          switch (attr) {
            case 'role':
              return this.getter(routeAtom).role;
            case 'displayName':
            case 'emailAddress':
            case 'isAuthenticated':
            case 'orgUid':
            case 'sessionId':
            case 'userName':
            case 'userId':
            case 'profiles':
            case 'otherAttributes':
              return getSessionProperty(this.platform, attr);
            case 'pageId':
            case 'appUid':
              return this.getter(rootPageAtom)[attr];
            default:
              if (attr.startsWith('otherAttributes_')) {
                const [, ...others] = attr.split('_');
                const key = others.join('_');
                const otherAttrs = getSessionProperty(
                  this.platform,
                  'otherAttributes',
                ) as string;
                if (otherAttrs) {
                  const jp = JSON.parse(otherAttrs);
                  return jp[key];
                }
              }
              throw Error(`Invalid session property ${attr}!`);
          }
        }
        case 'source': {
          const source = this.getter(sourceAtom);
          return source[attr];
        }
        case 'target': {
          const source = this.getter(targetAtom);
          return source[attr];
        }
        case '_': {
          const [, ...others] = path;
          return getIn(data, others, null);
        }
        case 'theme': {
          const [, ...others] = path;
          return getIn(this.getter(themeAtom), others, null);
        }
        default:
          break;
      }
    }

    if (_dsAlias) {
      const ds = this.platform.getDataSourceName(appUid, pageId, _dsAlias);
      if (!ds) {
        if (data[_dsAlias] !== undefined) {
          // could be a collectionRecord
        } else {
          throw Error(
            `Missing data store with alias ${_dsAlias} in page '${appUid}.${pageId}' [Variable: ${path.join(
              '.',
            )}]!`,
          );
        }
      }
      if (ds) {
        const store = getStore({ appUid, pageId, ds, alias: _dsAlias });
        if (!store) {
          throw Error(
            `Missing data store with alias ${_dsAlias} in page '${appUid}.${pageId}' [Variable: ${path.join(
              '.',
            )}]!`,
          );
        }
        switch (attr) {
          case '$size':
            return (
              this.getter<string[]>(store.recordsOrderPath) ?? EMPTY_ARRAY
            ).length;
          case '$totalRowCount':
            return this.getter<string[]>(store.totalRowCountPath) ?? 0;
          case '$selectedRowCount':
            return Object.keys(
              this.getter<Record<string, boolean>>(
                store.selectedRecordIdsPath,
              ) ?? EMPTY_OBJECT,
            ).length;
          case '$rows': {
            const records = this.getter<Records<never>>(store.recordsPath);
            if (!records) return EMPTY_ARRAY;
            return Object.values(records);
          }
          case '$selectedRows': {
            const records = this.getter<Records<never>>(store.recordsPath);
            const ids = records
              ? Object.keys(
                  this.getter<Record<string, boolean>>(
                    store.selectedRecordIdsPath,
                  ) ?? EMPTY_OBJECT,
                )
              : EMPTY_ARRAY;
            return ids.map((id) => records[id]);
          }
          case '$selectedRecordIds': {
            return Object.keys(
              this.getter<Record<string, boolean>>(
                store.selectedRecordIdsPath,
              ) ?? EMPTY_OBJECT,
            );
          }
          case '$dirtyRows': {
            const records = this.getter<Records<never>>(store.recordsPath);
            if (!records) return EMPTY_ARRAY;
            const dirtyRecords = Object.values(records).filter(
              (_record: Row<DataRecord>) =>
                _record._rs !== 'Q' && _record._rs !== 'N',
            );
            return dirtyRecords;
          }
          case '$dirty': {
            const origRecs = this.getter<Records<never>>(
              store.originalRecordsPath,
            );
            if (origRecs && Object.keys(origRecs).length > 0) {
              return true;
            }
            const records = this.getter<Records<never>>(store.recordsPath);
            if (!records) return false;
            const dirtyRecords = Object.values(records).filter(
              (_record: Row<DataRecord>) =>
                _record._rs !== 'Q' && _record._rs !== 'N',
            );
            return dirtyRecords.length > 0;
          }
          case '$busy':
            return this.getter(store.isBusyPath);
          case '$hasInitialQueryFired':
            return this.getter(store.initialQueryFiredPath);
          case '$hasFilters':
            return (
              (
                this.getter<Filters<unknown>>(store.gridFilterPath) ??
                EMPTY_ARRAY
              ).length > 0 ||
              (
                this.getter<Filters<unknown>>(store.smartSearchFilterPath) ??
                EMPTY_ARRAY
              ).length > 0
            );
          case '$currentRecordIndex': {
            const currentRecordId = this.getter<string>(
              store.currentRecordIdPath,
            );
            return this.getter<string[]>(store.recordsOrderPath).indexOf(
              currentRecordId,
            );
          }
          default:
            break;
        }
        let id: string;
        if (dsAlias === _dsAlias && recordId) {
          id = recordId;
        } else if (
          dsAlias === _dsAlias &&
          typeof rowIndex === 'number' &&
          rowIndex > -1
        ) {
          id =
            this.getter<string>([...store.recordsOrderPath, rowIndex]) ??
            `MISSING_ROW_${rowIndex}`;
        } else {
          id = this.getter(store.currentRecordIdPath) as string;
        }

        if (attr === '$current' || attr === '$isCurrentRecord') {
          const currentRecordId = this.getter<string>(
            store.currentRecordIdPath,
          );
          return id === currentRecordId;
        } else if (attr === '$isFromDB') {
          const record = this.platform.getter(store.recordPath, {
            params: { id },
          }) as Row<unknown>;
          return isFromDB(record);
        } else if (attr === '$orig') {
          if (attr2) {
            const origVal = this.getter(store.origAttributePath, {
              params: { id, attribute: attr2 },
            });
            if (origVal === NULL_SYMBOL) {
              return null;
            }
            return origVal;
          }
          return (
            this.getter(store.origPath, {
              params: { id },
            }) ?? {}
          );
        }

        let value;
        if (data.pivotColumn) {
          const record = data.record as Record<string, unknown>;
          value = record && record[attr];
        } else {
          value = this.getter(store.attributePath, {
            params: { id, attribute: attr },
          }) as Row<never>;
        }

        if (Logger.isLogEnabled()) {
          Logger.log({
            status: 'trace',
            itemType: 'VariableProvider',
            msg: `${path.join('.')}=${value}`,
            itemId: this.context.itemId,
            onRender: true,
          });
        }

        if (value !== undefined) {
          return value;
        }
      }
    }
    return getIn(data, path, undefined);
  }
}
